import React, { useState, useEffect, useMemo } from 'react';
import { FiEdit2, FiTrash2, FiEye, FiSearch, FiPlusCircle, FiFilter, FiBookOpen, FiUser, FiCalendar, FiHash, FiClock, FiExternalLink } from 'react-icons/fi';
import DataTable from '../../components/DataTable/DataTable';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import Select from '../../components/Select/Select';
import Pagination from '../../components/Pagination/Pagination';
import Card from '../../components/Card/Card';
import Modal from '../../components/Modal/Modal';
import api from '../../api';
import './Books.css';

const PAGE_SIZE = 30;

const Books = ({ onNavigate, showAlert, showConfirm }) => {
  const [books,         setBooks]         = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modalLoading,  setModalLoading]  = useState(false);
  
  const [search,        setSearch]        = useState('');
  const [category,      setCategory]      = useState('');
  const [subCategory,   setSubCategory]   = useState('');
  const [sortConfig,    setSortConfig]    = useState({ key: 'AccessionNo', direction: 'desc' });
  const [page,          setPage]          = useState(1);
  const [viewBook,      setViewBook]      = useState(null);

  /* ── Initial Data Fetch ── */
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [bookRes, catRes, subRes] = await Promise.all([
          api.get('/books.php'), api.get('/categories.php'), api.get('/subcategories.php')
        ]);
        const bookData = bookRes.data;
        const catData = catRes.data;
        const subData = subRes.data;
        if (bookData.error) throw new Error(bookData.error);
        setBooks(Array.isArray(bookData) ? bookData : []);
        setCategories(Array.isArray(catData) ? catData : []);
        setSubCategories(Array.isArray(subData) ? subData : []);
      } catch (err) {
        showAlert("Sync Error", `Books list could not be refreshed. \n${err.message}`, "confirm");
      } finally { setLoading(false); }
    };
    fetchData();
  }, [showAlert]);

  const handleViewDetails = async (accNo) => {
    setModalLoading(true);
    try {
      const res = await api.get(`/books.php?id=${accNo}`);
      const data = res.data;
      if (data.error) throw new Error(data.error);
      setViewBook(data);
    } catch (err) { showAlert("Error", "Could not fetch book details.", "confirm"); }
    finally { setModalLoading(false); }
  };

  const categoryOptions = useMemo(() => [{ value: '', label: 'All Categories' }, ...categories.map(c => ({ value: c.SubjectId, label: c.Name }))], [categories]);
  const subCategoryOptions = useMemo(() => {
    const filtered = category ? subCategories.filter(s => s.SubjectId == category) : subCategories;
    return [{ value: '', label: 'All Sub-Categories' }, ...filtered.map(s => ({ value: s.SubId, label: s.SubName }))];
  }, [subCategories, category]);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));

  const filtered = useMemo(() => {
    let data = [...books];
    if (search.trim()) {
      const q = search.toLowerCase();
      data = data.filter(b => (b.Title?.toLowerCase().includes(q)) || (b.Author?.toLowerCase().includes(q)) || (b.AccessionNo?.toLowerCase().includes(q)) || (b.ClassificationNo?.toString().includes(q)));
    }
    if (category) data = data.filter(b => b.SubjectId == category);
    if (subCategory) data = data.filter(b => b.SubId == subCategory);
    if (sortConfig.key) {
      data.sort((a, b) => {
        const aVal = a[sortConfig.key] || ''; const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return data;
  }, [books, search, category, subCategory, sortConfig]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = [
    { key: 'Title', label: 'Book Name', sortable: true, render: (val, row) => (
      <div className="book-cell"><div className="book-cell__cover">{val ? val.charAt(0) : 'B'}</div><div><p className="book-cell__title">{val || 'No Title'}</p><p className="book-cell__isbn">Acc No: {row.AccessionNo}</p></div></div>
    )},
    { key: 'Author', label: 'Author', sortable: true },
    { key: 'CategoryName', label: 'Category', sortable: true, render: (val) => <Badge variant="primary">{val || 'General'}</Badge> },
    { key: 'Year', label: 'Year', sortable: true },
    { key: 'actions', label: 'Actions', width: '120px', render: (_, row) => (
      <div className="table-actions">
        <button className="table-action-btn table-action-btn--view" onClick={() => handleViewDetails(row.AccessionNo)}><FiEye /></button>
        <button className="table-action-btn table-action-btn--edit" onClick={() => onNavigate?.('add-book', { edit: row.AccessionNo })}><FiEdit2 /></button>
        <button className="table-action-btn table-action-btn--delete" onClick={() => {
          showConfirm("Delete Book", "Are you sure you want to delete this book? History remains.", async () => {
             try {
               const res = await api.delete(`/books.php?id=${row.AccessionNo}`);
               const result = res.data;
               // if (!res.ok) handled by axios throwing error
               setBooks(prev => prev.filter(b => b.AccessionNo !== row.AccessionNo));
               showAlert("Deleted", "Book removed.", "success");
             } catch (err) { showAlert("Error", err.message, "confirm"); }
          });
        }}><FiTrash2 /></button>
      </div>
    )},
  ];

  return (
    <div className="books-page">
      <div className="page-header">
        <div><h2 className="page-header__title">Book Inventory</h2><p className="page-header__sub">Collection management and tracking</p></div>
        <Button variant="primary" icon={<FiPlusCircle />} onClick={() => onNavigate?.('add-book')}>Add New Book</Button>
      </div>

      <div className="books-page__summary">
        {[
          { label: 'Total Volumes', value: books.length, color: 'primary' },
          { label: 'Calculated At', value: new Date().toLocaleTimeString(), color: 'success' },
        ].map((chip) => (
          <div key={chip.label} className={`summary-chip summary-chip--${chip.color}`}><span className="summary-chip__value">{chip.value}</span><span className="summary-chip__label">{chip.label}</span></div>
        ))}
      </div>

      <Card padding="none">
        <div className="shared-toolbar">
          <div className="shared-search-box">
            <FiSearch className="shared-search-box__icon" />
            <input 
              type="text" 
              placeholder="Search title, author, accession no…" 
              value={search} 
              onChange={(e) => { setSearch(e.target.value); setPage(1); }} 
              className="shared-search-box__input" 
            />
          </div>
          <div className="books-toolbar__filters">
            <Select id="filter-category" options={categoryOptions} value={category} onChange={(e) => { setCategory(e.target.value); setSubCategory(''); setPage(1); }} className="toolbar-select" />
            <Select id="filter-subcategory" options={subCategoryOptions} value={subCategory} onChange={(e) => { setSubCategory(e.target.value); setPage(1); }} className="toolbar-select" />
          </div>
        </div>
        <DataTable columns={columns} data={paged} sortConfig={sortConfig} onSort={handleSort} loading={loading} />
        {totalPages > 1 && (<div className="books-pagination"><p className="books-pagination__info">Page {page} of {totalPages}</p><Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} /></div>)}
      </Card>

      <Modal isOpen={!!viewBook} onClose={() => setViewBook(null)} title="Full Book Information" size="lg" footer={<Button variant="primary" onClick={() => setViewBook(null)}>Close Info</Button>}>
        {viewBook && (
          <div className="book-detail-view">
            <div className="book-detail-header">
              <div className="book-detail-cover">{viewBook.Title?.charAt(0)}</div>
              <div className="book-detail-main">
                <h3 className="book-detail-title">{viewBook.Title}</h3>
                <p className="book-detail-author">By {viewBook.Author || 'Unknown Author'}</p>
                <div style={{display:'flex', gap:'8px', marginTop:'12px'}}>
                  <Badge variant="primary">{viewBook.CategoryName}</Badge>
                  <Badge variant="neutral">{viewBook.SubCategoryName}</Badge>
                </div>
              </div>
            </div>

            {/* Circulation Info Section */}
            {viewBook.IssueStatus && (
               <div className="circulation-banner circulation-banner--issued">
                  <div className="circulation-banner__icon"><FiClock /></div>
                  <div className="circulation-banner__content">
                    <h4>Currently Issued</h4>
                    <p>To <strong>{viewBook.IssuedToMember}</strong> (ID: {viewBook.IssuedToId})</p>
                    <div className="circulation-meta">
                       <span>Issued: {new Date(viewBook.IssueDate).toLocaleDateString()}</span>
                       <span>Due: {new Date(viewBook.DueDate).toLocaleDateString()}</span>
                       {viewBook.Fine > 0 && <span className="text-danger">Fine: ₹{viewBook.Fine}</span>}
                    </div>
                  </div>
                  <Button variant="primary" size="sm" icon={<FiExternalLink />} onClick={() => onNavigate?.('issue-return')}>Go to Return</Button>
               </div>
            )}

            <div className="detail-section">
              <h4 className="detail-section__title">Publication Details</h4>
              <div className="book-detail-grid">
                {[
                  { label: 'Accession No', val: viewBook.AccessionNo, icon: <FiHash /> },
                  { label: 'Classification', val: viewBook.ClassificationNo, icon: <FiBookOpen /> },
                  { label: 'Book Number', val: viewBook.BookNumber, icon: <FiHash /> },
                  { label: 'Edition', val: viewBook.Edition, icon: <FiCalendar /> },
                  { label: 'Publisher', val: viewBook.Publisher, icon: <FiUser /> },
                  { label: 'Year', val: viewBook.Year, icon: <FiCalendar /> },
                  { label: 'Pages', val: viewBook.Pages, icon: <FiBookOpen /> },
                  { label: 'Item Type', val: viewBook.ItemType, icon: <FiBookOpen /> },
                  { label: 'Volume', val: viewBook.Volumn, icon: <FiHash /> },
                  { label: 'Price (₹)', val: viewBook.Price, icon: <FiHash /> },
                ].map((item, idx) => (
                  <div key={idx} className="detail-item">
                    <div className="detail-item__icon">{item.icon}</div>
                    <div><span className="detail-item__label">{item.label}</span><span className="detail-item__value">{item.val || '—'}</span></div>
                  </div>
                ))}
              </div>
            </div>

            <div className="detail-section">
               <h4 className="detail-section__title">Abstract / Description</h4>
               <p className="detail-section__text">{viewBook.Description || 'No detailed description available for this volume.'}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Books;
